/**
 * @file ViewGroupDrawer.js
 * @description Side drawer that shows individuals belonging to a population group based on its rules.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { fetchPopulationGroupIndividuals } from '../../services/populationGroupService';
import { getIndividualRecord } from '../../services/individualService';
import Spinner from '../ui/spinners/Spinner';
import PopulationGroupDownloadMenu from './PopulationGroupDownloadMenu';
import styles from './PopulationGroup.module.scss';
import { getText } from '../../i18n';

function getAvatarSource(individual) {
    if (!individual) return '';
    return individual.avatarUrl ?? individual.tenantUsers?.[0]?.avatarUrl ?? '';
}

function getIndividualKey(individual) {
    return (
        individual?.id ??
        individual?.uniqueId ??
        `${individual?.firstName ?? ''}-${individual?.lastName ?? ''}`
    );
}

const ViewGroupDrawer = ({
    isOpen,
    closeDrawer,
    groupId,
    groupName,
    isDirty,
}) => {
    const [individuals, setIndividuals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [failedAvatarKeys, setFailedAvatarKeys] = useState({});
    const [avatarOverrides, setAvatarOverrides] = useState({});

    useEffect(() => {
        const getIndividuals = async () => {
            setIsLoading(true);
            try {
                const data = await fetchPopulationGroupIndividuals(
                    groupId,
                    1,
                    20,
                    '',
                    'nameasc',
                );

                setIndividuals(data.data);
            } catch (error) {
                console.error('Failed to fetch group individuals:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen && groupId) {
            setFailedAvatarKeys({});
            setAvatarOverrides({});
            getIndividuals();
        }
    }, [isOpen, groupId]);

    useEffect(() => {
        const loadMissingAvatars = async () => {
            const individualsMissingAvatar = individuals.filter(
                individual =>
                    !getAvatarSource(individual) && Boolean(individual?.id),
            );

            if (individualsMissingAvatar.length === 0) {
                return;
            }

            const avatarEntries = await Promise.all(
                individualsMissingAvatar.map(async individual => {
                    try {
                        const individualRecord = await getIndividualRecord(
                            individual.id,
                        );
                        const avatarSource = getAvatarSource(individualRecord);
                        return [getIndividualKey(individual), avatarSource];
                    } catch (error) {
                        return [getIndividualKey(individual), ''];
                    }
                }),
            );

            const avatarMap = {};
            avatarEntries.forEach(([individualKey, avatarSource]) => {
                if (avatarSource) {
                    avatarMap[individualKey] = avatarSource;
                }
            });

            if (Object.keys(avatarMap).length > 0) {
                setAvatarOverrides(previousState => ({
                    ...previousState,
                    ...avatarMap,
                }));
            }
        };

        if (isOpen && individuals.length > 0) {
            loadMissingAvatars();
        }
    }, [individuals, isOpen]);

    const handleAvatarLoadError = individual => {
        const individualKey = getIndividualKey(individual);
        setFailedAvatarKeys(previousState => ({
            ...previousState,
            [individualKey]: true,
        }));
    };

    const handleOverlayClick = () => {
        closeDrawer();
    };

    const handleDrawerClick = event => {
        event.stopPropagation();
    };

    const handleKeyDown = event => {
        if (event.key === 'Escape') {
            closeDrawer();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const drawerContent = (
        <>
            <div
                className={styles.drawerOverlay}
                onClick={handleOverlayClick}
                role="presentation"
            />
            <div
                className={styles.drawer}
                onClick={handleDrawerClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby="view-group-drawer-title"
            >
                <div className={styles.drawerHeader}>
                    <h5
                        id="view-group-drawer-title"
                        className={styles.drawerTitle}
                    >
                        {getText('msg_715a7e9b006f')}
                    </h5>
                    <div className={styles.drawerHeaderActions}>
                        <PopulationGroupDownloadMenu
                            populationGroup={{ id: groupId, name: groupName }}
                            variant="text"
                        />
                        <button
                            type="button"
                            className={styles.drawerCloseButton}
                            onClick={closeDrawer}
                            aria-label={getText('msg_de4e7f6fad95')}
                        >
                            <i className="bi bi-x me-1" />
                            {getText('msg_bbfa773e5a63')}
                        </button>
                    </div>
                </div>
                <div className={styles.drawerContent}>
                    {isDirty && (
                        <div className={styles.drawerDirtyWarning}>
                            <i className="bi bi-exclamation-triangle-fill" />
                            <span>{getText('msg_abc63a079ba9')}</span>
                        </div>
                    )}
                    <p className={styles.drawerSubtitle}>
                        {getText('msg_d4b3e651eb3a')}
                    </p>
                    {isLoading && (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Spinner width={48} height={48} />
                        </div>
                    )}
                    {!isLoading && individuals.length === 0 && (
                        <span
                            className={`text-muted ${styles.drawerEmptyState}`}
                        >
                            {getText('msg_251ca820d5b3')}
                        </span>
                    )}
                    {!isLoading && individuals.length > 0 && (
                        <ul className={styles.individualList}>
                            {individuals.map(individual => {
                                const individualKey =
                                    getIndividualKey(individual);
                                const avatarSource =
                                    avatarOverrides[individualKey] ??
                                    getAvatarSource(individual);
                                const shouldShowAvatar =
                                    Boolean(avatarSource) &&
                                    !failedAvatarKeys[individualKey];

                                return (
                                    <li
                                        key={individualKey}
                                        className={styles.individualItem}
                                    >
                                        {shouldShowAvatar ? (
                                            <img
                                                src={avatarSource}
                                                alt={`${individual.firstName} ${individual.lastName}`}
                                                className={
                                                    styles.individualAvatar
                                                }
                                                onError={() =>
                                                    handleAvatarLoadError(
                                                        individual,
                                                    )
                                                }
                                            />
                                        ) : (
                                            <div
                                                className={
                                                    styles.individualAvatarInitials
                                                }
                                            >
                                                {`${individual.firstName?.[0] ?? ''}${individual.lastName?.[0] ?? ''}`}
                                            </div>
                                        )}
                                        <div>
                                            <p
                                                className={`${styles.individualName} mb-0`}
                                            >
                                                {`${individual.firstName} ${individual.lastName}`}
                                            </p>
                                            <p
                                                className={`${styles.individualId} mb-0`}
                                            >
                                                {individual.uniqueId}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(drawerContent, document.body);
};

ViewGroupDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    groupName: PropTypes.string,
    isDirty: PropTypes.bool,
};

export default ViewGroupDrawer;
